import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a paginated blog posts list
 */
export default class BlogPostsList extends PaginatedList {
  connectedCallback() {
    super.connectedCallback?.();
    queueMicrotask(() => this.initializeStoryControls());
  }

  initializeStoryControls() {
    const grid = this.querySelector('.blog-posts-container');
    if (!grid) return;
    const sortOptions = [...this.querySelectorAll('[data-story-sort-option]')];
    const categoryOptions = [...this.querySelectorAll('[data-story-category-option]')];
    const datePattern = /(?:January|February|March|April|May|June|July|August|September|October|November|December|Januar|Februar|März|April|Mai|Juni|Juli|August|September|Oktober|November|Dezember|Jan|Feb|Mar|Apr|Jun|Jul|Aug|Sep|Sept|Oct|Nov|Dec)\s+\d{1,2},?\s+\d{4}/i;
    const items = [...grid.querySelectorAll(':scope > .blog-post-item')];
    items.forEach((item, originalIndex) => {
      const match = item.dataset.storySource?.match(datePattern);
      const publishedDate = item.querySelector('time')?.dateTime;
      const publishedTimestamp = publishedDate ? Date.parse(publishedDate) : NaN;
      const contentTimestamp = match
        ? Date.parse(match[0].replace(/(\d{1,2})\s+(\d{4})$/, '$1, $2'))
        : NaN;
      // Legacy stories may have been imported recently, so prefer an explicit
      // story date from the content before falling back to published_at.
      const timestamp = Number.isFinite(contentTimestamp) ? contentTimestamp : publishedTimestamp;
      if (Number.isFinite(timestamp)) {
        const date = new Date(timestamp);
        const time = item.querySelector('time');
        if (time) {
          time.dateTime = date.toISOString().slice(0, 10);
          time.textContent = new Intl.DateTimeFormat(document.documentElement.lang || 'en', {
            year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC'
          }).format(date);
        }
      }
      item.dataset.storyTimestamp = String(Number.isFinite(timestamp) ? timestamp : 0);
      item.dataset.storyOriginalIndex = String(originalIndex);
      const categoryText = item.querySelector('.aoi-blog-card-category')?.textContent || '';
      item.dataset.storyCategory = categoryText.split('/').pop()?.trim().toLowerCase() || 'exhibition';
    });

    let selectedSort = 'newest';
    let selectedCategory = 'all';
    const apply = () => this.applyStoryView(selectedSort, selectedCategory);
    sortOptions.forEach((button) => button.addEventListener('click', () => {
      selectedSort = button.dataset.storySortOption || 'newest';
      sortOptions.forEach((option) => option.setAttribute('aria-pressed', String(option === button)));
      apply();
    }));
    categoryOptions.forEach((button) => button.addEventListener('click', () => {
      selectedCategory = button.dataset.storyCategoryOption || 'all';
      categoryOptions.forEach((option) => option.setAttribute('aria-pressed', String(option === button)));
      apply();
    }));
    apply();
    this.classList.add('aoi-order-ready');
  }

  applyStoryView(order, selectedCategory) {
    const grid = this.querySelector('.blog-posts-container');
    if (!grid) return;
    const sectionId = this.getAttribute('section-id');
    const items = [...grid.querySelectorAll(':scope > .blog-post-item')];
    items.sort((a, b) => {
      const dateDifference = Number(a.dataset.storyTimestamp) - Number(b.dataset.storyTimestamp);
      const stableDifference = Number(a.dataset.storyOriginalIndex) - Number(b.dataset.storyOriginalIndex);
      return order === 'oldest' ? dateDifference || stableDifference : -dateDifference || stableDifference;
    });

    let visibleIndex = 0;
    items.forEach((item) => {
      const visible = selectedCategory === 'all' || item.dataset.storyCategory === selectedCategory;
      item.hidden = !visible;
      if (visible) {
        visibleIndex += 1;
        item.classList.toggle('blog-post-item--horizontal', visibleIndex === 1);
        item.dataset.blogIndex = `${sectionId}-${visibleIndex}`;
      } else {
        item.classList.remove('blog-post-item--horizontal');
      }
      grid.append(item);
    });

    const status = this.querySelector('[data-story-filter-status]');
    if (status) status.textContent = `${visibleIndex} ${visibleIndex === 1 ? 'story' : 'stories'}`;
  }
}

if (!customElements.get('blog-posts-list')) {
  customElements.define('blog-posts-list', BlogPostsList);
}
